import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {Link, Redirect, withRouter} from 'react-router-dom';

import api from 'utils/api';
import {USER_LANG} from 'utils/locale';
import {refreshProduct} from 'ducks/productDetail';
import {refreshProducts} from 'ducks/products';
import ProductQualityControlForm from 'components/ProductQualityControlForm';
import ProductDetailsSection from 'components/ProductDetailsSection';
import LoadingSpinner from 'components/LoadingSpinner';
import {configType} from 'propTypes/config';
import {productType} from 'propTypes/products';
import {QUALITY_CONTROL} from 'constants/production';
import ErrorAlert from './ErrorAlert';


class ProductQualityControl extends React.Component {
    static propTypes = {
        product: productType,
        config: configType.isRequired,
        error: PropTypes.string,
        isLoading: PropTypes.bool.isRequired,
        refreshProduct: PropTypes.func.isRequired,
        refreshProducts: PropTypes.func.isRequired,
        match: PropTypes.shape({
            params: PropTypes.shape({
                id: PropTypes.string.isRequired,
            }).isRequired,
        }).isRequired,
        isQualityAssuranceWorker: PropTypes.bool.isRequired,
        history: PropTypes.shape({
            push: PropTypes.func.isRequired,
        }).isRequired,
    };

    static defaultProps = {
        product: null,
        error: null,
    };

    componentDidMount() {
        this.props.refreshProduct(this.getProductId());
    }

    // eslint-disable-next-line react/prop-types
    getProductId = () => parseInt(this.props.match.params.id, 10);

    getPostData = formData => ({
        estimated_vs_actual: formData.estimated_vs_actual,
        estimated_vs_actual_salary_percentage: this.mapToSalaryPercentage(formData.estimated_vs_actual),
        tolerance: formData.tolerance,
        tolerance_salary_percentage: this.mapToSalaryPercentage(formData.tolerance),
        welding_quality: formData.welding_quality,
        welding_quality_salary_percentage: this.mapToSalaryPercentage(formData.welding_quality),
        action: formData.action,
        comment: formData.comment,
        next_status: formData.nextStatus.value,
    });

    mapToSalaryPercentage = (option) => {
        if (!option) {
            return 0;
        }
        const config = this.props.config;
        const mapToSalary = {
            [QUALITY_CONTROL.ESTIMATED_VS_ACTUAL_LESS_THAN_10]: config.EVA_LESS_THAN_10_SALARY_PERCENTAGE,
            [QUALITY_CONTROL.ESTIMATED_VS_ACTUAL_10_TO_20]: config.EVA_10_TO_20_SALARY_PERCENTAGE,
            [QUALITY_CONTROL.ESTIMATED_VS_ACTUAL_21_TO_30]: config.EVA_21_TO_30_SALARY_PERCENTAGE,
            [QUALITY_CONTROL.ESTIMATED_VS_ACTUAL_31_TO_40]: config.EVA_31_TO_40_SALARY_PERCENTAGE,
            [QUALITY_CONTROL.ESTIMATED_VS_ACTUAL_MORE_THAN_40]: config.EVA_MORE_THAN_40_SALARY_PERCENTAGE,
            [QUALITY_CONTROL.TOLERANCE_OK]: config.TOLERANCE_OK_SALARY_PERCENTAGE,
            [QUALITY_CONTROL.TOLERANCE_NOT_OK]: config.TOLERANCE_NOT_OK_SALARY_PERCENTAGE,
            [QUALITY_CONTROL.WELDING_QUALITY_GOOD]: config.WELDING_QUALITY_GOOD_SALARY_PERCENTAGE,
            [QUALITY_CONTROL.WELDING_QUALITY_NEUTRAL]: config.WELDING_QUALITY_NEUTRAL_SALARY_PERCENTAGE,
            [QUALITY_CONTROL.WELDING_QUALITY_BAD]: config.WELDING_QUALITY_BAD_SALARY_PERCENTAGE,
        };
        return mapToSalary[option];
    };

    handleFormSubmit = (values, handlers) => {
        // check if product is in progress while doing QA review
        api.productionProductDetail.fetch({pk: this.props.match.params.id}).then(
            (data) => {
                if (!data.active_worklog.in_progress) {
                    api.productionQualityControl.post(
                        // eslint-disable-next-line react/prop-types
                        {product_id: this.props.match.params.id},
                        this.getPostData(values),
                    ).then(
                        () => {
                            this.props.refreshProducts();
                            this.props.history.push(`/${USER_LANG}/production`);
                        },
                        (error) => {
                            handlers.setSubmitting(false);
                            if (error.isValidationError) {
                                const errors = this.mapServerErrors(JSON.parse(error.responseText));
                                handlers.setErrors(errors);
                            } else {
                                console.error(error);
                            }
                        },
                    );
                } else {
                    // redirect user to list of products where error message is displayed
                    this.props.history.push(`/${USER_LANG}/production`);
                }
            },
        ).catch(e => console.error(e));
    };

    mapServerErrors = (serverErrors) => {
        const errors = {};

        Object.keys(serverErrors).forEach((key) => {
            if (key === 'non_field_errors') {
                errors.nonFieldErrors = serverErrors[key];
            } else if (key === 'next_status') {
                errors.nextStatus = serverErrors[key][0];
            } else {
                errors[key] = serverErrors[key][0];
            }
        });

        return errors;
    };

    renderNavbar = () => {
        let text = gettext("Loading");
        if (this.props.error) {
            text = gettext('Error');
        } else if (!this.props.isLoading && this.props.product) {
            text = this.props.product.article_name;
        }
        return (
            <nav aria-label="breadcrumb">
                <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                        <Link to={`/${USER_LANG}/production`}>
                            {gettext('Production')}
                        </Link>
                    </li>
                    <li className="breadcrumb-item active">
                        {text} - {gettext('Quality control')}
                    </li>
                </ol>
            </nav>
        );
    };

    render() {
        if (!this.props.isQualityAssuranceWorker) {
            // We cannot create a custom react router component and put this
            // logic in there (like PrivateRoute), because the redirect did not
            // work then (got a "redirect to same route you're currently on
            // error).
            return <Redirect to={`/${USER_LANG}/production/auth`} />;
        }

        let content = null;
        if (this.props.error) {
            // Loading has failed
            content = <ErrorAlert>{this.props.error}</ErrorAlert>;
        } else if (this.props.isLoading) {
            // Loading in progress
            content = <LoadingSpinner />;
        } else if (this.props.product) {
            // Got a product
            content = (
                <div className="card">
                    <div className="card-body">
                        <ProductDetailsSection product={this.props.product} />

                        <ProductQualityControlForm
                            product={this.props.product}
                            config={this.props.config}
                            onSubmit={this.handleFormSubmit}
                        />
                    </div>
                </div>
            );
        }

        return (
            <div>
                {this.renderNavbar()}
                {content}
            </div>
        );
    }
}

const mapStateToProps = state => ({
    product: state.productDetail.product,
    error: state.productDetail.error,
    isLoading: state.productDetail.isLoading,
    isQualityAssuranceWorker: state.workers.worker && state.workers.worker.role === 'quality',
});

const mapDispatchToProps = dispatch => ({
    refreshProduct: productId => dispatch(refreshProduct(productId)),
    refreshProducts: () => dispatch(refreshProducts()),
});

export default withRouter(connect(
    mapStateToProps,
    mapDispatchToProps,
)(ProductQualityControl));



// WEBPACK FOOTER //
// ./src/components/ProductQualityControl.js